import React, { useState, useEffect, useRef } from "react"
import { Bars3Icon, ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon, SparklesIcon, BanknotesIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import TypeResolver from "aldoo-ra/CMS/type-resolver"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { useFieldDragController } from "aldoo-ra/CMS/controllers/field-drag-controller"
import { showNotification } from "aldoo-ra/Notification"
import Typography from "aldoo-ra/Typography"
import { priceDisplayFormat, getPaymentDuration } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import PhaseSlotDisplay from "aldoo-ra/CMSModels/SubscriptionPhase/components/PhaseSlotDisplay"
import PlatformProductEditor from "aldoo-ra/CMSModels/PlatformProduct/editors/platform-product-editor"
import { PlatformProductType } from "aldoo-ra/CMSModels/PlatformProduct"
import Tooltip from "aldoo-ra/Tooltip"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"
import _ from "lodash"
import { currencyOptions as storeCurrencyOptions } from "aldoo-ra/Store/currencies"
import { useStoreCurrency } from "aldoo-ra/Store/currency"

/**
 * Phase configuration dialog component
 */
function PhaseConfigDialog({ slot, phaseData, priceModel, onSave, onDelete, onClose, parentProductType }) {
  // a new phase is priced in the store's currency (STORE_CURRENCY) unless its model says otherwise
  const { currency: storeCurrency, currencies: storeCurrencies } = useStoreCurrency()
  const [localPhaseData, setLocalPhaseData] = useState(phaseData || {})
  const [displayAmount, setDisplayAmount] = useState('')
  const [displayIterations, setDisplayIterations] = useState('')
  const [displayIntervalCount, setDisplayIntervalCount] = useState('')
  const [pricingModel, setPricingModel] = useState('recurring') // 'one_time' or 'recurring'
  const priceInputRef = useRef(null)

  // Update local state when phaseData prop changes
  useEffect(() => {
    // Determine pricing model type from phase data or parent product type
    let detectedPricingModel = 'recurring'

    // Auto-detect from product type
    if (parentProductType === 'purchase') {
      detectedPricingModel = 'one_time'
    } else if (parentProductType === 'subscription') {
      detectedPricingModel = 'recurring'
    } else if (slot?.type === 'regular') {
      // For license or other types, detect from phase data
      if (phaseData?.interval) {
        detectedPricingModel = 'recurring'
      } else if (phaseData && !phaseData.interval) {
        detectedPricingModel = 'one_time'
      }
    }

    setPricingModel(detectedPricingModel)

    // Initialize localPhaseData with defaults for subscription phases
    const defaultPhaseData = {
      phase_type: slot?.type || 'regular',
      enabled: true,
      amount: 0,
      currency: priceModel?.currency || storeCurrency,
      requires_payment_method: true,
      ...phaseData // Override with actual data if provided
    }

    // Add subscription-specific defaults only for recurring model
    if (detectedPricingModel === 'recurring' && (slot?.type === 'trial' || slot?.type === 'introductory' || slot?.type === 'regular')) {
      defaultPhaseData.interval = defaultPhaseData.interval || 'month'
      defaultPhaseData.interval_count = defaultPhaseData.interval_count || 1
    }

    // For trial/intro phases, add iterations default
    if (slot?.type === 'trial' || slot?.type === 'introductory') {
      defaultPhaseData.iterations = defaultPhaseData.iterations || 1
    }

    setLocalPhaseData(defaultPhaseData)

    // Set display amount as-is (backend stores as decimal, not cents)
    if (phaseData?.amount !== undefined) {
      setDisplayAmount(phaseData.amount.toString())
    } else {
      setDisplayAmount('')
    }
    // Set displayIterations
    if (phaseData?.iterations !== undefined && phaseData?.iterations !== null) {
      setDisplayIterations(phaseData.iterations.toString())
    } else {
      setDisplayIterations('')
    }
    // Set displayIntervalCount
    if (phaseData?.interval_count !== undefined && phaseData?.interval_count !== null) {
      setDisplayIntervalCount(phaseData.interval_count.toString())
    } else {
      setDisplayIntervalCount('')
    }
    // Focus the price input when dialog opens
    setTimeout(() => {
      priceInputRef.current?.focus()
    }, 100)
  }, [phaseData, slot, priceModel, parentProductType])

  const isExistingPhase = phaseData?.phase_type === slot?.type

  const handleFieldChange = (field, value) => {
    setLocalPhaseData(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = (dataOverride) => {
    // Use dataOverride if provided, otherwise use localPhaseData
    let dataToSave = dataOverride !== undefined ? dataOverride : localPhaseData

    // Validate required fields - 0 is a valid amount (free)
    // Only check for undefined or null, not 0 or empty string that could be parsed as 0
    if (dataToSave.amount === undefined || dataToSave.amount === null) {
      showNotification({
        message: 'Price amount is required',
        className: 'bg-admin_error text-white'
      })
      return
    }

    // Apply pricing model type to phase data
    if (pricingModel === 'one_time' && slot?.type === 'regular') {
      // One-time payment: clear interval data
      dataToSave = {
        ...dataToSave,
        interval: null,
        interval_count: null,
        iterations: null
      }
    } else if (pricingModel === 'recurring' && slot?.type === 'regular') {
      // Recurring payment: ensure interval data exists
      if (!dataToSave.interval) {
        dataToSave = {
          ...dataToSave,
          interval: 'month',
          interval_count: 1
        }
      }
    }

    onSave(dataToSave)
  }

  const intervalOptions = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' }
  ]

  // the virtual currency is stored lower case ('coins'), the money ones upper
  // the store's currencies (SUPPORTED_CURRENCIES), and coins
  const currencyOptions = storeCurrencyOptions({ virtual: true, currencies: storeCurrencies })

  const pricingModelOptions = [
    { value: 'one_time', label: 'One Time Payment' },
    { value: 'recurring', label: 'Recurring' }
  ]

  // Disable pricing model selection for purchase/subscription types (auto-determined)
  const isPricingModelLocked = parentProductType === 'purchase' || parentProductType === 'subscription'

  return (
    <div className="p-6 min-w-[500px]">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="h5" className="text-admin_text font-semibold">
          {slot?.label} Price
        </Typography>

        <div className="flex items-center gap-4">
          {/* Enabled Checkbox - Hide for regular phase (always required) */}
          {slot?.type !== 'regular' && (
            <Checkbox
              isOn={localPhaseData.enabled !== false}
              onChange={(checked) => handleFieldChange('enabled', checked)}
              label="Enabled"
              variant="admin"
            />
          )}
        </div>
      </div>

      <div className="space-y-4">

        {/* Amount and Currency Row - Hide for trial phases */}
        {slot?.type !== 'trial' && (
          <div className="flex gap-4">
            {/* Amount */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-admin_text mb-2">
                Price Amount ({(localPhaseData.currency || storeCurrency || '').toUpperCase()})
              </label>
              <Input
                ref={priceInputRef}
                value={displayAmount}
                onChange={(e) => {
                  // Allow free editing - just update display value
                  setDisplayAmount(e.target.value)
                }}
                onBlur={(e) => {
                  // On blur, validate and save as decimal (not cents)
                  const val = e.target.value
                  if (val === '' || val === null || val === undefined) {
                    handleFieldChange('amount', 0)
                    setDisplayAmount('0')
                    return
                  }
                  const amount = parseFloat(val.replace(',', '.'))
                  if (!isNaN(amount)) {
                    handleFieldChange('amount', amount)
                    setDisplayAmount(amount.toString())
                  } else {
                    // Invalid input, reset to current value
                    setDisplayAmount(localPhaseData.amount !== undefined ? localPhaseData.amount.toString() : '0')
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    // Manually process the value and save immediately
                    const val = e.target.value
                    let amount = 0

                    if (val !== '' && val !== null && val !== undefined) {
                      const parsed = parseFloat(val.replace(',', '.'))
                      if (!isNaN(parsed)) {
                        amount = parsed
                      }
                    }

                    // Create updated data with the new amount
                    const updatedData = { ...localPhaseData, amount }
                    // Save directly with the updated data
                    handleSave(updatedData)
                  }
                }}
                placeholder="0.00"
                variant="admin"
              />
            </div>

            {/* Currency */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-admin_text mb-2">
                Currency
              </label>
              <DropdownSelect
                value={[{ value: (localPhaseData.currency || storeCurrency || '').toUpperCase(), label: (localPhaseData.currency || storeCurrency || '').toUpperCase() }]}
                onChange={(val) => handleFieldChange('currency', val[0]?.value)}
                options={currencyOptions}
                variant="admin"
              />
            </div>
          </div>
        )}

        {/* For Trial/Intro: Duration selector, For Regular: Billing Interval */}
        {slot?.type !== 'regular' ? (
          <>
            {/* Duration Row: Number and Unit */}
            <div className="flex gap-4">
              {/* Duration Number */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Duration
                </label>
                <Input
                  type="number"
                  value={displayIterations}
                  onChange={(e) => {
                    setDisplayIterations(e.target.value)
                  }}
                  onBlur={(e) => {
                    const val = e.target.value
                    if (val === '' || val === null || val === undefined) {
                      handleFieldChange('iterations', 1)
                      setDisplayIterations('1')
                      return
                    }
                    const iterations = parseInt(val)
                    if (!isNaN(iterations) && iterations > 0) {
                      handleFieldChange('iterations', iterations)
                      setDisplayIterations(iterations.toString())
                    } else {
                      setDisplayIterations(localPhaseData.iterations !== undefined ? localPhaseData.iterations.toString() : '1')
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      const val = e.target.value
                      let iterations = 1

                      if (val !== '' && val !== null && val !== undefined) {
                        const parsed = parseInt(val)
                        if (!isNaN(parsed) && parsed > 0) {
                          iterations = parsed
                        }
                      }

                      const updatedData = { ...localPhaseData, iterations }
                      handleSave(updatedData)
                    }
                  }}
                  placeholder="1"
                  variant="admin"
                />
              </div>

              {/* Duration Unit */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-admin_text mb-2">
                  Unit
                </label>
                <DropdownSelect
                  value={[{ value: localPhaseData.interval || 'day', label: localPhaseData.interval || 'day' }]}
                  onChange={(val) => {
                    handleFieldChange('interval', val[0]?.value)
                    handleFieldChange('interval_count', 1) // Always 1 for trial/intro
                  }}
                  options={intervalOptions}
                  variant="admin"
                />
              </div>
            </div>

            {/* Requires Payment Method */}
            <div>
              <Checkbox
                isOn={localPhaseData.requires_payment_method !== false}
                onChange={(checked) => handleFieldChange('requires_payment_method', checked)}
                label="Requires Payment Method"
                variant="admin"
              />
            </div>
          </>
        ) : (
          <>
            {/* Billing Interval and Interval Count Row - Show only for recurring pricing model */}
            {pricingModel === 'recurring' && (
              <div className="flex gap-4">
                {/* Interval Count */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-admin_text mb-2">
                    Interval Count
                  </label>
                  <Input
                    type="number"
                    value={displayIntervalCount}
                    onChange={(e) => {
                      setDisplayIntervalCount(e.target.value)
                    }}
                    onBlur={(e) => {
                      const val = e.target.value
                      if (val === '' || val === null || val === undefined) {
                        handleFieldChange('interval_count', 1)
                        setDisplayIntervalCount('1')
                        return
                      }
                      const interval_count = parseInt(val)
                      if (!isNaN(interval_count) && interval_count > 0) {
                        handleFieldChange('interval_count', interval_count)
                        setDisplayIntervalCount(interval_count.toString())
                      } else {
                        setDisplayIntervalCount(localPhaseData.interval_count !== undefined ? localPhaseData.interval_count.toString() : '1')
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        const val = e.target.value
                        let interval_count = 1

                        if (val !== '' && val !== null && val !== undefined) {
                          const parsed = parseInt(val)
                          if (!isNaN(parsed) && parsed > 0) {
                            interval_count = parsed
                          }
                        }

                        const updatedData = { ...localPhaseData, interval_count }
                        handleSave(updatedData)
                      }
                    }}
                    placeholder="1"
                    variant="admin"
                  />
                </div>

                {/* Billing Interval */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-admin_text mb-2">
                    Billing Interval
                  </label>
                  <DropdownSelect
                    value={[{ value: localPhaseData.interval || 'month', label: localPhaseData.interval || 'month' }]}
                    onChange={(val) => handleFieldChange('interval', val[0]?.value)}
                    options={intervalOptions}
                    variant="admin"
                  />
                </div>
              </div>
            )}

            {/* Bottom row: Requires Payment Method and Pricing Model */}
            <div className="flex gap-4">
              {/* Requires Payment Method - Show for all pricing models */}
              <div className="flex-1">
                <Checkbox
                  isOn={localPhaseData.requires_payment_method !== false}
                  onChange={(checked) => handleFieldChange('requires_payment_method', checked)}
                  label="Requires Payment Method"
                  variant="admin"
                />
              </div>

              {/* Pricing Model Dropdown - Show only for regular phase */}
              {slot?.type === 'regular' && (
                <div className="flex-1">
                  <label className="block text-sm font-medium text-admin_text mb-2">
                    Pricing Model
                  </label>
                  <DropdownSelect
                    value={[pricingModelOptions.find(opt => opt.value === pricingModel)]}
                    onChange={(val) => {
                      if (!isPricingModelLocked) {
                        setPricingModel(val[0]?.value)
                      }
                    }}
                    options={pricingModelOptions}
                    variant="admin"
                    disabled={isPricingModelLocked}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex justify-between items-center mt-8 pt-4 border-t border-admin_bordered">
        {/* Delete Phase button - Hide for purchase products (only one phase required) */}
        {isExistingPhase && slot?.isPurchase !== true ? (
          <Button
            variant="admin_secondary"
            className="text-admin_error border-admin_error hover:bg-admin_error hover:text-white"
            onClick={onDelete}
          >
            Delete Phase
          </Button>
        ) : (
          <div></div>
        )}

        <button
          onClick={() => handleSave()}
          className="h-[40px] flex items-center justify-center bg-admin_primary hover:bg-admin_primary_dk text-white font-bold rounded-full py-2 px-4 transition duration-200 ease-in-out w-[120px]"
        >
          Save
        </button>
      </div>
    </div>
  )
}

function PriceModelEditor({
  onChange,
  value,
  field,
  parentValues,
  parentObject,
}) {
  const [localValue, setLocalValue] = useState(value ?? [])
  // new prices and phases are in the store's currency (STORE_CURRENCY)
  const { currency: storeCurrency } = useStoreCurrency()
  const [showPhaseDialog, setShowPhaseDialog] = useState(false)
  const [phaseSlotConfig, setPhaseSlotConfig] = useState({ priceModelIndex: null, slot: null, phaseData: {} })
  const [collapsedItems, setCollapsedItems] = useState(() => {
    // Initialize items - primary expanded, rest collapsed
    const initial = {}
    ;(value ?? []).forEach((item, index) => {
      initial[index] = !item.primary // Collapse everything except primary
    })
    return initial
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [platformFilter, setPlatformFilter] = useState([])
  const [phaseFilter, setPhaseFilter] = useState([])
  const inputRefs = useRef([])
  const isInternalChange = useRef(false)
  const itemRefs = useRef([])
  const [hasAutoAdded, setHasAutoAdded] = useState(false)

  const labelField = field?.item?.labelField || "label"
  const { getContentType } = TypeResolver()

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    // Determine product type
    const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
    const parentTypeRaw = parentObjectData?.type || (typeof parentValues === 'function' ? parentValues()?.type : parentValues?.type)
    const parentType = typeof parentTypeRaw === 'object' ? parentTypeRaw?.value : parentTypeRaw
    const isPurchaseProduct = parentType === 'purchase'

    // Sync root-level fields from regular phase for each price model (only for subscriptions)
    const syncedValue = newValue.map(priceModel => {
      // For purchase products, don't sync from phases - use root-level fields directly
      if (isPurchaseProduct) {
        return priceModel
      }

      // Check if this price model has phases with a regular phase
      if (priceModel.phases && Array.isArray(priceModel.phases)) {
        const regularPhase = priceModel.phases.find(p => p.phase_type === 'regular' && p.enabled !== false)

        if (regularPhase) {
          // If regular phase has an interval, this is a subscription - sync from phases
          if (regularPhase.interval) {
            // Calculate duration from regular phase interval
            let duration
            if (regularPhase.interval === 'month' && regularPhase.interval_count === 1) {
              duration = 'month'
            } else if (regularPhase.interval === 'year' && regularPhase.interval_count === 1) {
              duration = 'year'
            } else if (regularPhase.interval === 'week' && regularPhase.interval_count === 1) {
              duration = 'week'
            } else {
              // Custom duration in days
              duration = 'custom'
            }

            // Sync amount, currency, duration, and type from regular phase to root level
            // Note: type should be 'real' (not 'subscription') to match the backend enum
            return {
              ...priceModel,
              amount: regularPhase.amount,
              currency: regularPhase.currency,
              duration: duration,
              duration_in_days: duration === 'custom' ? (regularPhase.interval_count * (regularPhase.interval === 'day' ? 1 : regularPhase.interval === 'week' ? 7 : regularPhase.interval === 'month' ? 30 : 365)) : undefined,
              type: 'real'
            }
          }

          // No interval on the regular phase means One Time Payment — a lifetime
          // entitlement. The root-level duration/auto_renew are legacy mirrors of
          // the phase and MUST be cleared here: leaving a stale recurring term
          // behind makes the backend treat a one-off as an expiring subscription.
          return {
            ...priceModel,
            amount: regularPhase.amount,
            currency: regularPhase.currency,
            duration: 0,
            duration_in_days: undefined,
            auto_renew: false,
            type: 'real'
          }
        }
      }

      return priceModel
    })

    isInternalChange.current = true
    setLocalValue(syncedValue)
    onChange?.(syncedValue)
  }

  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue) // Updated to use updateValue

  const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObjectData)
      : field.required

  // Migrate old format to new phases format
  const migrateToPhases = (priceModel) => {
    // Helper to interpret duration field
    // Handles both string format ('month', 'year', 'custom') and numeric format (30, 365, other)
    const parseDuration = (duration, customDaysField) => {
      // Handle string format
      if (typeof duration === 'string') {
        if (duration === 'month') {
          return { interval: 'month', interval_count: 1 }
        } else if (duration === 'year') {
          return { interval: 'year', interval_count: 1 }
        } else if (duration === 'custom' && customDaysField) {
          return { interval: 'day', interval_count: customDaysField }
        } else if (duration === 'no-trial' || duration === 'one-time-payment') {
          return null
        } else {
          return { interval: 'month', interval_count: 1 }
        }
      }
      // Handle numeric format
      if (typeof duration === 'number') {
        if (duration === 0) {
          return null
        } else if (duration === 30) {
          return { interval: 'month', interval_count: 1 }
        } else if (duration === 365) {
          return { interval: 'year', interval_count: 1 }
        } else {
          return { interval: 'day', interval_count: duration }
        }
      }
      return null
    }

    if (!priceModel.phases || priceModel.phases.length === 0) {
      const phases = []

      // Create trial phase if trial_duration exists and is not 'no-trial' or 0
      const trialDuration = parseDuration(priceModel.trial_duration, priceModel.trial_duration_in_days)
      if (trialDuration) {
        phases.push({
          enabled: true,
          phase_type: 'trial',
          amount: 0,
          currency: priceModel.currency || storeCurrency,
          iterations: trialDuration.interval_count,  // Use interval_count as iterations (e.g., 14 days = 14 iterations of 1 day)
          interval: trialDuration.interval,
          interval_count: 1,  // Always 1 for trial/intro phases
          requires_payment_method: priceModel.requires_payment_method !== false
        })
      }

      // Create regular phase from root amount/currency/duration
      const regularDuration = parseDuration(priceModel.duration, priceModel.duration_in_days)
      if (regularDuration) {
        phases.push({
          enabled: true,
          phase_type: 'regular',
          amount: priceModel.amount || 0,
          currency: priceModel.currency || storeCurrency,
          iterations: null,
          interval: regularDuration.interval,
          interval_count: regularDuration.interval_count,
          requires_payment_method: priceModel.requires_payment_method !== false
        })
      } else if (priceModel.duration === 'one-time-payment' || priceModel.duration === 0) {
        // One-time purchases carry no recurring term. Both the legacy string
        // marker and a numeric duration of 0 mean lifetime, and each needs a
        // regular phase without an interval — otherwise the editor renders no
        // regular phase at all for correctly configured lifetime products.
        phases.push({
          enabled: true,
          phase_type: 'regular',
          amount: priceModel.amount || 0,
          currency: priceModel.currency || storeCurrency,
          iterations: null,
          interval: null,
          interval_count: null,
          requires_payment_method: priceModel.requires_payment_method !== false
        })
      }

      return { ...priceModel, phases }
    }

    // If phases exist but regular phase is missing amount/currency, inherit from root
    const updatedPhases = priceModel.phases.map(phase => {
      if (phase.phase_type === 'regular') {
        return {
          ...phase,
          amount: phase.amount ?? priceModel.amount ?? 0,
          currency: phase.currency ?? priceModel.currency ?? storeCurrency
        }
      }
      return phase
    })

    return { ...priceModel, phases: updatedPhases }
  }

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      // Don't process undefined - wait for actual data to load
      if (value === undefined) {
        return
      }

      let migratedValue = (value ?? []).map(migrateToPhases)

      // If array is empty, add one default item
      if (migratedValue.length === 0) {
        // Determine parent product type to set appropriate default for auto_renew
        const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
        const parentTypeRaw =
          parentObjectData?._parentProduct?.type ||
          parentObjectData?.type ||
          (typeof parentValues === 'function' ? parentValues()?.type : parentValues?.type)
        const parentType = typeof parentTypeRaw === 'object' ? parentTypeRaw?.value : parentTypeRaw
        const isSubscription = parentType === 'subscription'

        const defaultItem = {
          type: 'real',
          currency: storeCurrency,
          amount: 0,
          duration: 'one-time-payment',
          trial_duration: 'no-trial',
          auto_renew: isSubscription, // Default to true for subscriptions, false for purchases/licenses
          primary: true,
          requires_payment_method: true,
          real_product_id: [],
          phases: []
        }
        migratedValue = [defaultItem]
        // Notify parent of the change
        isInternalChange.current = true
        setTimeout(() => {
          updateValue(migratedValue)
        }, 0)
      }

      setLocalValue(migratedValue)
      // Reset collapsed state - primary expanded, rest collapsed
      const newCollapsed = {}
      migratedValue.forEach((item, index) => {
        newCollapsed[index] = !item.primary // Collapse everything except primary
      })
      setCollapsedItems(newCollapsed)
    }
    isInternalChange.current = false
  }, [value])

  // a price added before the store's currency was known (still at 0, with
  // none) takes it once it is
  useEffect(() => {
    if (!storeCurrency) return
    const unset = (item) => item.type === 'real' && !item.currency && !item.amount
    if (!localValue.some(unset)) return
    updateValue(localValue.map((item) => (unset(item) ? { ...item, currency: storeCurrency } : item)))
  }, [storeCurrency, localValue])

  // Auto-add first item if array is empty on mount
  // Use a ref to track if we should still auto-add
  const shouldAutoAddRef = useRef(true)

  useEffect(() => {
    // Cancel auto-add if localValue gets populated
    if (localValue.length > 0) {
      shouldAutoAddRef.current = false
    }
  }, [localValue.length])

  useEffect(() => {
    if (!hasAutoAdded && localValue.length === 0) {
      const timeoutId = setTimeout(() => {
        // Only add if we still should (localValue hasn't been populated)
        if (shouldAutoAddRef.current) {
          setHasAutoAdded(true)
          addNewInput()
        }
      }, 150)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [hasAutoAdded, localValue.length])

  const updateName = (label) => {
    return label
      .toLowerCase()
      .replace(/ /g, "_")
      .replace(/[^a-zA-Z0-9_]/g, "")
  }

  const autoResolveFields = (index) => {
    if (field.item.autoID === false) return

    const updatedValues = [...localValue]
    const value = updatedValues[index]
    if (value[labelField] && !value.id) value.id = updateName(value[labelField])
    updateValue(updatedValues)
  }

  const handleValueChange = (updatedValues, index) => {
    if (index === undefined) {
      updateValue([
        ...localValue,
        {
          [labelField]: updatedValues,
        },
      ])
      return
    }
    const newValues = [...localValue]
    newValues[index][labelField] = updatedValues
    updateValue(newValues)

    inputRefs.current[index]?.focus()
  }

  const addNewInput = () => {
    // Determine parent product type to set appropriate default for auto_renew
    const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
    const parentTypeRaw =
      parentObjectData?._parentProduct?.type ||
      parentObjectData?.type ||
      (typeof parentValues === 'function' ? parentValues()?.type : parentValues?.type)
    const parentType = typeof parentTypeRaw === 'object' ? parentTypeRaw?.value : parentTypeRaw
    const isSubscription = parentType === 'subscription'

    const newItem = {
      type: 'real',
      currency: storeCurrency,
      amount: 0,
      duration: 'one-time-payment',
      trial_duration: 'no-trial',
      auto_renew: isSubscription, // Default to true for subscriptions, false for purchases/licenses
      requires_payment_method: true,
      real_product_id: [],
      phases: [],
      // If this is the first item, make it primary by default
      primary: localValue.length === 0
    }
    const newIndex = localValue.length
    updateValue([...localValue, newItem])
    // Set new item as expanded (not collapsed)
    setCollapsedItems(prev => ({ ...prev, [newIndex]: false }))

    // Scroll to the newly added item after a short delay to ensure it's rendered
    setTimeout(() => {
      itemRefs.current[newIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })
    }, 100)
  }

  const removeInput = (index) => {
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
  }

  const clearAll = () => {
    updateValue([])
  }

  const handlePhaseSlotClick = (priceModelIndex, slot, isPurchase = false) => {
    const priceModel = localValue[priceModelIndex]
    const phases = priceModel.phases || []
    const existingPhase = phases.find(p => p.phase_type === slot.type)

    // Add isPurchase flag to slot
    const slotWithContext = { ...slot, isPurchase }

    // For purchase products with regular phase, use root-level amount/currency
    if (isPurchase && slot.type === 'regular') {
      setPhaseSlotConfig({
        priceModelIndex,
        slot: slotWithContext,
        phaseData: {
          phase_type: 'regular',
          enabled: true,
          amount: priceModel.amount || 0,
          currency: priceModel.currency || storeCurrency,
          interval: 'month',
          interval_count: 1,
          iterations: null,
          requires_payment_method: true
        },
        priceModel
      })
    } else if (existingPhase) {
      // Edit existing phase (for subscription products)
      setPhaseSlotConfig({
        priceModelIndex,
        slot: slotWithContext,
        phaseData: existingPhase,
        priceModel
      })
    } else {
      // Create new phase with defaults (for subscription products)
      setPhaseSlotConfig({
        priceModelIndex,
        slot: slotWithContext,
        phaseData: {
          phase_type: slot.type,
          enabled: true,
          amount: 0,
          currency: storeCurrency,
          interval: 'month',
          interval_count: 1,
          iterations: slot.type === 'regular' ? null : 1,
          requires_payment_method: true
        },
        priceModel
      })
    }

    setShowPhaseDialog(true)
  }

  const handleSavePhase = (updatedPhaseData) => {
    const { priceModelIndex, slot } = phaseSlotConfig
    const updatedValues = [...localValue]
    const priceModel = updatedValues[priceModelIndex]

    // For purchase products, update root-level amount/currency/type instead of phases
    if (slot.isPurchase && slot.type === 'regular') {
      priceModel.amount = updatedPhaseData.amount
      priceModel.currency = updatedPhaseData.currency
      priceModel.type = 'real'
      // Don't create phases array for purchase products — but a regular phase
      // it already has follows the price: the labels (the product's Price
      // Configuration, the model's header) read it, and a stale one kept
      // showing the old amount after a change
      if (Array.isArray(priceModel.phases)) {
        priceModel.phases = priceModel.phases.map((phase) =>
          phase.phase_type === 'regular'
            ? { ...phase, amount: updatedPhaseData.amount, currency: updatedPhaseData.currency }
            : phase
        )
      }
    } else {
      // For subscription products, use phases structure
      const phases = priceModel.phases || []
      const existingIndex = phases.findIndex(p => p.phase_type === slot.type)

      if (existingIndex >= 0) {
        phases[existingIndex] = updatedPhaseData
      } else {
        phases.push(updatedPhaseData)
      }

      priceModel.phases = phases
    }

    updateValue(updatedValues)
    setShowPhaseDialog(false)
  }

  const handleDeletePhase = () => {
    const { priceModelIndex, slot } = phaseSlotConfig
    const updatedValues = [...localValue]
    const priceModel = updatedValues[priceModelIndex]

    priceModel.phases = (priceModel.phases || []).filter(p => p.phase_type !== slot.type)
    updateValue(updatedValues)
    setShowPhaseDialog(false)
  }

  const handleCopyProductId = async (productId, platform) => {
    try {
      await navigator.clipboard.writeText(productId)
      showNotification({
        message: `${platform} product ID copied: ${productId}`,
        className: "bg-admin_success text-white",
      })
    } catch (error) {
      showNotification({
        message: "Failed to copy product ID",
        className: "bg-admin_error text-white",
      })
    }
  }

  const getTrialPeriod = (item) => {
    const { trial_duration, trial_duration_in_days } = item

    if (!trial_duration || trial_duration === "no-trial") {
      return null
    }

    // Handle custom duration or numeric values
    if (trial_duration === "custom" || !isNaN(parseInt(trial_duration))) {
      const days = parseInt(trial_duration_in_days || trial_duration)
      if (isNaN(days)) {
        return null
      }
      if (days === 7) {
        return "1 week"
      } else if (days === 14) {
        return "2 weeks"
      } else if (days === 30) {
        return "1 month"
      } else {
        return `${days} day${days !== 1 ? 's' : ''}`
      }
    }

    // For predefined durations like "week", "month", "year"
    return `1 ${trial_duration}`
  }

  const renderPriceInfo = (item) => {
    if (!item?.currency) return <span className="text-admin_text_secondary">Add a price model</span>

    const isSubscription = item.duration !== undefined && item.duration !== "one-time-payment"
    const isFree = parseInt(item.amount) === 0

    if (isFree) {
      return (
        <div className="flex items-center gap-2">
          {item.primary && (
            <span className="bg-admin_primary text-white px-2 py-1 rounded text-xs font-medium">
              Primary
            </span>
          )}
          <span className="text-lg font-bold text-admin_text">Free</span>
        </div>
      )
    }

    const paymentType = isSubscription ? "Subscription" : "One time Purchase"
    const paymentDuration = getPaymentDuration(item.duration, item.duration_in_days)
    const trial = isSubscription ? getTrialPeriod(item) : null
    const requiresPayment = isSubscription ? item.requires_payment_method : null

    return (
      <div className="flex flex-col gap-2">
        {/* Main price info */}
        <div className="flex items-center gap-2 flex-wrap">
          {item.primary && (
            <span className="bg-admin_primary text-white px-2 py-1 rounded text-xs font-medium">
              Primary
            </span>
          )}
          <span className="text-admin_text">
            <span className="font-medium">{paymentType}</span> for{' '}
            <span className="font-bold text-lg">{priceDisplayFormat(item.amount, item.currency)}</span>
            {paymentDuration && <span className="text-admin_text_secondary"> {paymentDuration}</span>}
          </span>
        </div>
        
        {/* Trial and payment info */}
        {(trial || requiresPayment !== null) && (
          <div className="flex flex-col gap-1">
            {trial && (
              <span className="text-sm text-admin_text_secondary">
                Trial: {trial}
              </span>
            )}
            {requiresPayment !== null && (
              <span className="text-xs text-admin_text_secondary">
                {requiresPayment ? "Requires Payment Method" : "No Credit Card Required"}
              </span>
            )}
          </div>
        )}
      </div>
    )
  }


  const renderItem = (value, index) => {
    // Always show phases section in card view for easy access
    // The phase slot editor will handle Web platform validation internally
    const hasPhases = true

    // Determine allowed phases based on parent product type
    // For 'purchase' type products, only show regular phase (no trial/intro)
    // Check _parentProduct first (when editing from StoreProduct), then check parentObject/parentValues
    const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
    const parentTypeRaw =
      parentObjectData?._parentProduct?.type ||
      parentObjectData?.type ||
      (typeof parentValues === 'function' ? parentValues()?.type : parentValues?.type)
    const parentType = typeof parentTypeRaw === 'object' ? parentTypeRaw?.value : parentTypeRaw
    const isPurchaseProduct = parentType === 'purchase'
    const allowedPhases = isPurchaseProduct ? ['regular'] : ['trial', 'introductory', 'regular']

    const pricingTitle = generatePricingTitle(value)
    const isCollapsed = collapsedItems[index]

    return (
      <div
        key={index}
        ref={(el) => (itemRefs.current[index] = el)}
        className="w-full mt-3 first:mt-0"
        onDragOver={localValue?.length > 1 ? (e) => {
          e.preventDefault()
          handleDragOver(index)
        } : undefined}
      >
        <div className={`border ${value.primary ? 'border-admin_primary/30 border-2' : 'border-admin_bordered'} rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden`}>
          {/* Card Header - Paper background, clickable to toggle */}
          <div
            className="bg-admin_paper p-4 cursor-pointer flex items-center justify-between"
            onClick={() => setCollapsedItems(prev => ({ ...prev, [index]: !prev[index] }))}
          >
            <div className="flex items-center gap-2">
              {localValue.length > 1 && (
                <div
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation()
                    handleDragStart(index)
                  }}
                  onDragEnd={handleDragEnd}
                  className="cursor-move"
                >
                  <Bars3Icon className="w-5 h-5 text-admin_text_secondary flex-shrink-0" />
                </div>
              )}
              {pricingTitle && (
                <>
                  <span className="text-sm font-semibold text-admin_text">
                    {pricingTitle.priceText}
                  </span>

                  {pricingTitle.trialChip && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-admin_success text-white rounded">
                      {pricingTitle.trialChip}
                    </span>
                  )}
                  {pricingTitle.introChip && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-admin_progress text-white rounded">
                      {pricingTitle.introChip}
                    </span>
                  )}
                </>
              )}
              {value.primary && (
                <Tooltip content="This is currently live on the stores" position="top">
                  <span className="px-2 py-0.5 text-xs font-medium bg-admin_primary text-white rounded-full">
                    Primary
                  </span>
                </Tooltip>
              )}
            </div>

            {/* Right side: Platform icons, Make Primary button, and Collapse/Expand Icon */}
            <div className="flex items-center gap-3">
              {/* Platform icons */}
              {value.real_product_id && Array.isArray(value.real_product_id) && value.real_product_id.length > 0 && (
                <div className="flex items-center gap-2">
                  {[...new Set(value.real_product_id.map(p => p.platform).filter(Boolean))].map((platform) => (
                    <Tooltip key={platform} content={getPlatformDisplayName(platform)} position="top">
                      <div className="flex items-center justify-center flex-shrink-0">
                        {renderPlatformIcon(platform.toLowerCase(), "w-5 h-5 text-admin_text_secondary")}
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}

              {!value.primary && !isCollapsed && (
                <Button
                  variant="admin_secondary"
                  onClick={(e) => {
                    e.stopPropagation()
                    // Set this item as primary and unset others
                    const updatedValues = localValue.map((item, i) => ({
                      ...item,
                      primary: i === index
                    }))
                    updateValue(updatedValues)
                  }}
                  className="text-xs px-2 py-1 h-auto"
                >
                  Make Primary
                </Button>
              )}
              {isCollapsed ? (
                <ChevronDownIcon className="w-5 h-5 text-admin_text_secondary" />
              ) : (
                <ChevronUpIcon className="w-5 h-5 text-admin_text_secondary" />
              )}
            </div>
          </div>

          {/* Card Body - Canvas background */}
          {!isCollapsed && (
          <div className="bg-admin_canvas p-4">
            <div className="flex flex-col gap-4">
              {/* Top: Phase Slots - Interactive */}
              {hasPhases && (
                <div className="flex-1 border-b border-admin_bordered pb-8">
                  <Typography
                    variant="admin_txt1_dim"
                    className="block font-medium uppercase tracking-wide mb-4"
                  >
                    Purchase Phases
                  </Typography>

                  <PhaseSlotDisplay
                    phases={isPurchaseProduct && value.amount !== undefined ? [{
                      phase_type: 'regular',
                      amount: value.amount,
                      currency: value.currency,
                      enabled: true
                    }] : value.phases}
                    onClick={(slot) => handlePhaseSlotClick(index, slot, isPurchaseProduct)}
                    compact={false}
                    allowedPhases={allowedPhases}
                  />
                </div>
              )}

              {/* Bottom: Platform Product IDs - Full editor with add button and validation */}
              <div className="flex-1 w-full">
                <Typography
                  variant="admin_txt1_dim"
                  className="font-medium mb-2 uppercase tracking-wide"
                >
                  Platform Product IDs
                </Typography>

                <PlatformProductEditor
                  key={`platform-products-${value._id || value.id || index}`}
                  value={value.real_product_id}
                  onChange={(newProducts) => {
                    const updatedValues = [...localValue]
                    updatedValues[index] = { ...updatedValues[index], real_product_id: newProducts }
                    updateValue(updatedValues)
                  }}
                  field={{
                    id: 'real_product_id',
                    type: 'Array',
                    item: PlatformProductType
                  }}
                  parentObject={parentObject}
                  parentValues={parentValues}
                />
              </div>
            </div>

            {/* Delete button at bottom right */}
            <div className="flex justify-end mt-4 pt-4 border-t border-admin_bordered">
              <Button
                variant="admin_error"
                confirm={true}
                confirmMessage={`Remove this price model from the list?`}
                onClick={(e) => {
                  e.stopPropagation()
                  removeInput(index)
                }}
              >
                Delete
              </Button>
            </div>
          </div>
          )}
        </div>
      </div>
    )
  }

  const validators = (errors) => ({
    type: (value, contentType) => {
      if (
        (contentType.type == "Array" || contentType.type == "Content") &&
        !getContentType(contentType.item)
      ) {
        errors.item = "Missing or invalid content type"
      }
    },
  })

  // Helper function to generate pricing title data (reused for display and search)
  const generatePricingTitle = (priceModel) => {
    const phases = priceModel?.phases
    if (!phases || !Array.isArray(phases) || phases.length === 0) {
      return null
    }

    const enabledPhases = phases.filter(p => p.enabled !== false)
    const trial = enabledPhases.find(p => p.phase_type === 'trial')
    const introductory = enabledPhases.find(p => p.phase_type === 'introductory')
    const regular = enabledPhases.find(p => p.phase_type === 'regular')

    if (!regular) return null

    const formatInterval = (phase) => {
      const count = phase.interval_count || 1
      const interval = phase.interval || 'month'
      return count === 1 ? interval : `${count} ${interval}s`
    }

    const formatDuration = (phase) => {
      const iterations = phase.iterations
      const count = phase.interval_count || 1
      const interval = phase.interval || 'month'
      if (!iterations) return null
      const totalUnits = iterations * count
      return totalUnits === 1 ? `${totalUnits} ${interval}` : `${totalUnits} ${interval}s`
    }

    // Build structured data
    const result = {
      priceText: '',
      trialChip: null,
      introChip: null,
      fullText: '' // For search
    }

    if (regular.amount === 0) {
      result.priceText = 'Free'
      result.fullText = 'Free'
      return result
    }

    const regularPrice = priceDisplayFormat(regular.amount, regular.currency)

    // Check if this is a one-time purchase (no interval) or subscription
    if (regular.interval) {
      const regularInterval = formatInterval(regular)
      result.priceText = `${regularPrice}/${regularInterval}`
    } else {
      // One-time purchase - just show the price
      result.priceText = regularPrice
    }

    let textParts = [result.priceText]

    if (trial && trial.amount === 0) {
      const trialDuration = formatDuration(trial)
      if (trialDuration) {
        result.trialChip = `${trialDuration} trial`
        textParts.push(`with ${trialDuration} trial`)
      }
    }

    if (introductory) {
      const introPrice = priceDisplayFormat(introductory.amount, introductory.currency)
      const introDuration = formatDuration(introductory)
      if (introDuration) {
        result.introChip = `${introPrice} introductory`
        textParts.push(`${introPrice} for the first ${introDuration}`)
      }
    }

    result.fullText = textParts.join('. ')
    return result
  }

  // Filter items based on search term, platform filter, and phase filter
  const filteredValue = localValue.filter((item, index) => {
    // Platform filter check
    if (platformFilter && platformFilter.length > 0) {
      const selectedPlatforms = platformFilter.map(p => p.value)
      const hasPlatform = item.real_product_id && Array.isArray(item.real_product_id) &&
        item.real_product_id.some(p => selectedPlatforms.includes(p.platform))
      if (!hasPlatform) return false
    }

    // Phase filter check
    if (phaseFilter && phaseFilter.length > 0) {
      const selectedPhases = phaseFilter.map(p => p.value)
      const phases = item.phases || []

      // For one-time-payment items without phases array, treat them as "regular" phase
      const isPurchase = item.duration === 'one-time-payment'
      if (isPurchase && phases.length === 0) {
        // One-time payments (including free) are considered "regular" phase items
        if (!selectedPhases.includes('regular')) {
          // Doesn't match any selected phase filter (trial/introductory)
          return false
        }
        // This is a one-time payment matching "regular" filter, continue to other filters
      } else {
        // Check if item has any of the selected phase types in phases array
        const hasPhase = selectedPhases.some(phaseType => {
          return phases.some(phase => {
            return phase.phase_type === phaseType && phase.enabled !== false
          })
        })

        if (!hasPhase) return false
      }
    }

    // Search term check
    if (!searchTerm) return true

    const search = searchTerm.toLowerCase()

    // Search in platform product IDs
    if (item.real_product_id && Array.isArray(item.real_product_id)) {
      const foundInProductIds = item.real_product_id.some(platformProduct => {
        const productId = (platformProduct.id || '')?.toLowerCase()
        const platform = (platformProduct.platform || '')?.toLowerCase()
        return productId.includes(search) || platform.includes(search)
      })
      if (foundInProductIds) return true
    }

    // Search in pricing title
    const pricingTitle = generatePricingTitle(item)
    if (pricingTitle && pricingTitle.fullText && pricingTitle.fullText.toLowerCase().includes(search)) {
      return true
    }

    return false
  })

  return (
    <div className="array-editor">
      {/* Header with search and buttons */}
      <div className="relative w-full flex items-center justify-between mb-4">
        <span className="absolute -top-8 right-0 text-sm text-admin_text bg-admin_info_bg rounded-full px-5 py-0.5">
          {filteredValue.length === localValue.length
            ? `${localValue.length} ${localValue.length === 1 ? "item" : "items"}`
            : `${filteredValue.length} of ${localValue.length} ${localValue.length === 1 ? "item" : "items"}`
          }
        </span>

        <div className="relative w-full flex items-center gap-3">
          {/* Platform Filter */}
          <DropdownSelect
            variant="admin"
            options={[
              { value: "iOS", name: "iOS" },
              { value: "Android", name: "Android" },
              { value: "Web", name: "Web" },
              { value: "Windows", name: "Windows" },
              { value: "Mac", name: "Mac" },
            ]}
            multiSelection
            placeholder="Filter by platform"
            className="w-[180px] whitespace-nowrap"            
            value={platformFilter}
            onChange={(value) => setPlatformFilter(value)}
            showCheckbox={false}
            showSelectionTick={true}
            onItemRender={(platform) => (
              <div className="flex items-center gap-2">
                {renderPlatformIcon(platform.value.toLowerCase(), "w-4 h-4 text-admin_text_secondary")}

                <span className="text-sm text-admin_text">
                  {platform.name}
                </span>
              </div>
            )}
            onValueItemRender={(platform) => {
              if (!platform) return "Filter by platform"
              return (
                <div className="flex items-center gap-2">
                  {renderPlatformIcon(platform.value.toLowerCase(), "w-4 h-4 text-admin_text_secondary")}
                  <span className="text-sm">
                    {platform.name}
                  </span>
                </div>
              )
            }}
          />

          {/* Phase Filter */}
          <DropdownSelect
            variant="admin"
            className="w-[180px] whitespace-nowrap" 
            options={[
              { value: "trial", name: "Trial", icon: MagnifyingGlassIcon },
              { value: "introductory", name: "Introductory", icon: SparklesIcon },
              { value: "regular", name: "Regular", icon: BanknotesIcon },
            ]}
            multiSelection
            placeholder="Filter by phase"
            value={phaseFilter}
            onChange={(value) => setPhaseFilter(value)}
            showCheckbox={false}
            showSelectionTick={true}
            onItemRender={(phase) => {
              const PhaseIcon = phase.icon
              return (
                <div className="flex items-center gap-2">
                  <PhaseIcon className="w-4 h-4 text-admin_text_secondary" />
                  <span className="text-sm text-admin_text">
                    {phase.name}
                  </span>
                </div>
              )
            }}
            onValueItemRender={(phase) => {
              if (!phase) return "Filter by phase"
              const PhaseIcon = phase.icon
              return (
                <div className="flex items-center gap-2">
                  <PhaseIcon className="w-4 h-4 text-admin_text_secondary" />
                  <span className="text-sm">
                    {phase.name}
                  </span>
                </div>
              )
            }}
          />

          {/* Search input */}
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search platform IDs..."
            variant="admin"
            showClearButton={true}
            onClear={() => setSearchTerm('')}
            className="w-full xl:w-[460px] 2xl:w-[512px]"
          />

          <div className="flex gap-3 ml-auto">
            {/* Add Item button */}
            <Tooltip content="Add Item" position="top">
              <Button
                variant="admin_secondary"
                className="w-10 flex-shrink-0 p-0"
                onClick={addNewInput}
              >
                <PlusIcon className="w-5 h-5"/>
              </Button>
            </Tooltip>

            {/* Clear All button */}
            <Tooltip content="Clear All" position="top">
              <Button
                variant="admin_secondary"
                className="w-10 flex-shrink-0 p-0"
                onClick={clearAll}
                confirm={true}
                confirmMessage="Remove all price models from the list?"
              >
                <TrashIcon className="w-5 h-5" />
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="bg-admin_canvas rounded-2xl p-4 max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_canvas">
        {filteredValue.length > 0 ? (
          filteredValue.map((value, index) => {
            // Find the original index in localValue for proper handling
            const originalIndex = localValue.findIndex(v => v === value)
            return renderItem(value, originalIndex)
          })
        ) : localValue.length > 0 ? (
          <div className="text-center py-8 text-admin_text_secondary">
            <p>No matching price models found</p>
          </div>
        ) : (
          <div className="text-center py-8 text-admin_text_secondary">
            <p>No price models added yet</p>
          </div>
        )}
      </div>

      {showPhaseDialog && (
        <Modal
          isOpen={showPhaseDialog}
          onClose={() => setShowPhaseDialog(false)}
          closeOnEscape
          variant="admin_centered"
        >
          <PhaseConfigDialog
            key={`${phaseSlotConfig.priceModelIndex}-${phaseSlotConfig.slot?.type}`}
            slot={phaseSlotConfig.slot}
            phaseData={phaseSlotConfig.phaseData}
            priceModel={phaseSlotConfig.priceModel}
            parentProductType={(() => {
              const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
              const parentTypeRaw =
                parentObjectData?._parentProduct?.type ||
                parentObjectData?.type ||
                (typeof parentValues === 'function' ? parentValues()?.type : parentValues?.type)
              return typeof parentTypeRaw === 'object' ? parentTypeRaw?.value : parentTypeRaw
            })()}
            onSave={handleSavePhase}
            onDelete={handleDeletePhase}
            onClose={() => setShowPhaseDialog(false)}
          />
        </Modal>
      )}
    </div>
  )
}

export default PriceModelEditor
